// === GÉNÉRATEUR DE TUILES PIXEL ART POUR L'ÎLE ===
// Tiles 32×32 générées via SVG inline, palette pirate sombre cohérente avec le reste du jeu.
// Sprint A1 : 5 types (sable, herbe/jungle, chemin, eau, rocher) + 2 frames de capitaine.
//
// Inspiré de SpriteRenderer (même pattern : SVG → canvas texture, pixelArt = true côté Phaser).

import Phaser from 'phaser';

// ============================================================
// PALETTE — couleurs pirate sombres
// ============================================================

const PALETTE = {
  // Eau profonde — bleu nuit
  waterDeep:     '#0e2040',
  waterMid:      '#1a3458',
  waterShallow:  '#2a4a70',
  waterFoam:     '#c8d8e8',

  // Sable de plage
  sandBase:      '#c9a979',
  sandLight:     '#d8bb8c',
  sandDark:      '#a88a5a',
  sandShell:     '#ecdcb5',

  // Herbe / jungle
  grassBase:     '#3d5a35',
  grassLight:    '#4f7045',
  grassDark:     '#2a3f25',
  grassDetail:   '#1f3a1e',

  // Chemin / sentier
  pathBase:      '#7a6240',
  pathLight:     '#8e7551',
  pathDark:      '#5d4a30',

  // Rocher
  rockBase:      '#5b6470',
  rockLight:     '#7a8390',
  rockDark:      '#3d454f',

  // Bois (poutres, planches, tonneaux)
  woodBase:      '#6b4a2b',
  woodLight:     '#8a6438',
  woodDark:      '#4a3220',

  // Toits & toiles
  thatchBase:    '#8b6f3a',
  thatchLight:   '#a98a4c',
  thatchDark:    '#5e4a26',
  canvasRed:     '#8b3a2b',
  canvasRedDark: '#5e271d',

  // Métal / forge
  metalBase:     '#7a8390',
  metalLight:    '#a1abb5',
  metalDark:     '#3d454f',
  forgeGlow:     '#e67e22',
  forgeBright:   '#f1c40f',

  // Pierre (fondations)
  stoneBase:     '#5d5d5d',
  stoneLight:    '#7a7a7a',
  stoneDark:     '#3d3d3d',
} as const;

export const TILE_SIZE = 32;

// ============================================================
// SVG TEMPLATES — chaque tile renvoie un SVG 32×32 pixel art
// ============================================================

const TILES: Record<string, () => string> = {
  // Sable — base claire, quelques grains plus sombres et un coquillage
  sand: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.sandBase}"/>
    <rect x="0" y="0" width="32" height="32" fill="${PALETTE.sandLight}" opacity="0.25"/>
    <rect x="4" y="6" width="2" height="2" fill="${PALETTE.sandDark}"/>
    <rect x="22" y="3" width="2" height="2" fill="${PALETTE.sandDark}"/>
    <rect x="14" y="18" width="2" height="2" fill="${PALETTE.sandDark}"/>
    <rect x="27" y="24" width="2" height="2" fill="${PALETTE.sandDark}"/>
    <rect x="8" y="26" width="2" height="2" fill="${PALETTE.sandDark}"/>
    <rect x="18" y="10" width="3" height="2" fill="${PALETTE.sandShell}" opacity="0.7"/>
    <rect x="18" y="9" width="3" height="1" fill="${PALETTE.sandShell}" opacity="0.5"/>
  `),

  // Herbe / jungle — texture en touffes pixel
  grass: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.grassBase}"/>
    <rect x="2" y="3" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="3" y="2" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="10" y="6" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="11" y="5" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="19" y="4" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="26" y="9" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="5" y="14" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="15" y="12" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="23" y="17" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="8" y="20" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="19" y="22" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="28" y="25" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="4" y="26" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="13" y="27" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="7" y="9" width="1" height="1" fill="${PALETTE.grassDetail}"/>
    <rect x="22" y="13" width="1" height="1" fill="${PALETTE.grassDetail}"/>
    <rect x="11" y="24" width="1" height="1" fill="${PALETTE.grassDetail}"/>
  `),

  // Chemin / sentier — terre tassée
  path: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.pathBase}"/>
    <rect x="0" y="0" width="32" height="32" fill="${PALETTE.pathLight}" opacity="0.15"/>
    <rect x="3" y="5" width="3" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="14" y="3" width="2" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="23" y="8" width="3" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="7" y="14" width="2" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="18" y="16" width="3" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="4" y="22" width="3" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="15" y="25" width="2" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="25" y="22" width="3" height="2" fill="${PALETTE.pathDark}"/>
    <rect x="10" y="9" width="1" height="1" fill="${PALETTE.pathLight}"/>
    <rect x="20" y="12" width="1" height="1" fill="${PALETTE.pathLight}"/>
    <rect x="11" y="18" width="1" height="1" fill="${PALETTE.pathLight}"/>
    <rect x="22" y="27" width="1" height="1" fill="${PALETTE.pathLight}"/>
  `),

  // Eau — vagues stylisées, fond bleu nuit (frame 1)
  water: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.waterDeep}"/>
    <rect x="0" y="0" width="32" height="32" fill="${PALETTE.waterMid}" opacity="0.4"/>
    <rect x="2" y="6" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="14" y="4" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="23" y="9" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="4" y="14" width="4" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="13" y="17" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="24" y="20" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="2" y="24" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="14" y="27" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="22" y="29" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="6" y="6" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.6"/>
    <rect x="16" y="17" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.5"/>
    <rect x="26" y="9" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.6"/>
  `),

  // Eau — frame 2 : vagues décalées d'une à deux colonnes vers la droite, écume
  // de positions différentes. Le toggle frame1/frame2 toutes les ~1,1 s donne
  // l'illusion d'un défilement organique sans coût mémoire.
  water_alt: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.waterDeep}"/>
    <rect x="0" y="0" width="32" height="32" fill="${PALETTE.waterMid}" opacity="0.4"/>
    <rect x="5" y="5" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="17" y="3" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="26" y="10" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="7" y="13" width="4" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="16" y="16" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="27" y="21" width="4" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="5" y="23" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="17" y="26" width="5" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="25" y="28" width="6" height="1" fill="${PALETTE.waterShallow}"/>
    <rect x="3" y="10" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.55"/>
    <rect x="20" y="14" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.6"/>
    <rect x="11" y="22" width="2" height="1" fill="${PALETTE.waterFoam}" opacity="0.5"/>
  `),

  // Rocher — bloc gris avec relief
  rock: () => svg(`
    <rect width="32" height="32" fill="${PALETTE.grassBase}"/>
    <rect x="2" y="3" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="28" y="2" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="29" y="28" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="3" y="29" width="1" height="1" fill="${PALETTE.grassLight}"/>
    <rect x="6" y="10" width="20" height="16" fill="${PALETTE.rockBase}"/>
    <rect x="8" y="8" width="16" height="2" fill="${PALETTE.rockBase}"/>
    <rect x="4" y="14" width="2" height="10" fill="${PALETTE.rockBase}"/>
    <rect x="26" y="14" width="2" height="10" fill="${PALETTE.rockBase}"/>
    <rect x="7" y="26" width="18" height="2" fill="${PALETTE.rockBase}"/>
    <rect x="8" y="10" width="14" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="6" y="12" width="2" height="6" fill="${PALETTE.rockLight}"/>
    <rect x="11" y="14" width="2" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="22" y="14" width="2" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="18" y="18" width="2" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="24" y="16" width="2" height="8" fill="${PALETTE.rockDark}"/>
    <rect x="20" y="22" width="6" height="2" fill="${PALETTE.rockDark}"/>
    <rect x="8" y="22" width="6" height="2" fill="${PALETTE.rockDark}"/>
    <rect x="7" y="24" width="18" height="2" fill="${PALETTE.rockDark}"/>
  `),
};

// ============================================================
// CAPITAINE — sprite top-down 24×24, 2 frames (idle + walk)
// ============================================================

const CAPTAIN_SIZE = 24;
const CAPTAIN: Record<string, () => string> = {
  // Frame idle — vue dessus : tricorne noir + crâne + manteau + bottes
  idle: () => svgN(CAPTAIN_SIZE, `
    <!-- Ombre au sol -->
    <ellipse cx="12" cy="20" rx="7" ry="2" fill="#000000" opacity="0.35"/>
    <!-- Manteau (épaules, vue top-down) -->
    <rect x="6" y="11" width="12" height="8" fill="#1a3a5c"/>
    <rect x="5" y="12" width="14" height="6" fill="#1a3a5c"/>
    <rect x="6" y="11" width="1" height="8" fill="#0e2a45"/>
    <rect x="17" y="11" width="1" height="8" fill="#0e2a45"/>
    <!-- Ligne dorée centrale (boutonnage du manteau, à peine visible vue dessus) -->
    <rect x="11" y="13" width="2" height="4" fill="#f1c40f" opacity="0.4"/>
    <!-- Tricorne noir vue dessus : forme triangulaire 3 cornes -->
    <rect x="7" y="6" width="10" height="5" fill="#0f0f0f"/>
    <rect x="6" y="7" width="12" height="3" fill="#0f0f0f"/>
    <rect x="5" y="8" width="14" height="1" fill="#0f0f0f"/>
    <!-- Détail tricorne : crâne doré au centre -->
    <rect x="11" y="8" width="2" height="2" fill="#f1c40f"/>
    <rect x="11" y="8" width="1" height="1" fill="#f5d76e"/>
    <!-- Visage entrevu sous le tricorne -->
    <rect x="9" y="10" width="6" height="2" fill="#d4a574"/>
    <!-- Sabre côté droit (lueur dorée) -->
    <rect x="18" y="12" width="1" height="4" fill="#bdc3c7"/>
    <rect x="18" y="11" width="2" height="1" fill="#f1c40f"/>
    <!-- Bottes (en bas) -->
    <rect x="8" y="19" width="3" height="2" fill="#0f0f0f"/>
    <rect x="13" y="19" width="3" height="2" fill="#0f0f0f"/>
  `),

  // Frame walk — bras et bottes décalés (effet pas)
  walk: () => svgN(CAPTAIN_SIZE, `
    <ellipse cx="12" cy="20" rx="7" ry="2" fill="#000000" opacity="0.35"/>
    <rect x="6" y="11" width="12" height="8" fill="#1a3a5c"/>
    <rect x="5" y="13" width="14" height="5" fill="#1a3a5c"/>
    <rect x="6" y="11" width="1" height="8" fill="#0e2a45"/>
    <rect x="17" y="11" width="1" height="8" fill="#0e2a45"/>
    <rect x="11" y="13" width="2" height="4" fill="#f1c40f" opacity="0.4"/>
    <rect x="7" y="6" width="10" height="5" fill="#0f0f0f"/>
    <rect x="6" y="7" width="12" height="3" fill="#0f0f0f"/>
    <rect x="5" y="8" width="14" height="1" fill="#0f0f0f"/>
    <rect x="11" y="8" width="2" height="2" fill="#f1c40f"/>
    <rect x="11" y="8" width="1" height="1" fill="#f5d76e"/>
    <rect x="9" y="10" width="6" height="2" fill="#d4a574"/>
    <rect x="18" y="13" width="1" height="4" fill="#bdc3c7"/>
    <rect x="18" y="12" width="2" height="1" fill="#f1c40f"/>
    <!-- Bottes décalées : gauche en avant, droite en arrière -->
    <rect x="7" y="19" width="3" height="2" fill="#0f0f0f"/>
    <rect x="14" y="20" width="3" height="2" fill="#0f0f0f"/>
  `),
};

// ============================================================
// MOUETTES — sprites 16×8, 2 frames de battement d'ailes (Sprint A4)
// ============================================================
// Vue de dessus à 30° : aile vers le haut (frame 1) puis vers le bas (frame 2).
// Volent en orbite déterministe au-dessus de l'île, projettent une petite ombre
// au sol. Animation purement décorative.

const SEAGULL_W = 16;
const SEAGULL_H = 8;

/** Variante de svgN avec H différent de W (pour la mouette rectangulaire 16×8). */
function svgRect(w: number, h: number, inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" shape-rendering="crispEdges">${inner}</svg>`;
}

const SEAGULLS: Record<string, () => string> = {
  // Ailes vers le haut
  up: () => svgRect(SEAGULL_W, SEAGULL_H, `
    <!-- Corps -->
    <rect x="7" y="3" width="2" height="2" fill="#ffffff"/>
    <rect x="6" y="4" width="4" height="1" fill="#ecdcb5"/>
    <!-- Ailes hautes -->
    <rect x="3" y="2" width="3" height="1" fill="#ffffff"/>
    <rect x="2" y="1" width="2" height="1" fill="#ffffff"/>
    <rect x="10" y="2" width="3" height="1" fill="#ffffff"/>
    <rect x="12" y="1" width="2" height="1" fill="#ffffff"/>
    <!-- Bec orange minuscule -->
    <rect x="9" y="4" width="1" height="1" fill="#e67e22"/>
  `),
  // Ailes vers le bas
  down: () => svgRect(SEAGULL_W, SEAGULL_H, `
    <rect x="7" y="3" width="2" height="2" fill="#ffffff"/>
    <rect x="6" y="4" width="4" height="1" fill="#ecdcb5"/>
    <rect x="3" y="5" width="3" height="1" fill="#ffffff"/>
    <rect x="2" y="6" width="2" height="1" fill="#ffffff"/>
    <rect x="10" y="5" width="3" height="1" fill="#ffffff"/>
    <rect x="12" y="6" width="2" height="1" fill="#ffffff"/>
    <rect x="9" y="4" width="1" height="1" fill="#e67e22"/>
  `),
};

// ============================================================
// SURVIVANTS — sprites top-down 24×24, 2 frames × 3 classes (Sprint A3)
// ============================================================
// Cohérent avec le capitaine (même taille, même style) mais distinctif :
//   - warrior : bandana rouge, tunique sombre, sabre
//   - ranger  : capuche verte, tunique marron, arc dans le dos
//   - medic   : bonnet blanc à croix rouge, tablier blanc, sacoche

const SURVIVOR_SIZE = 24;

/** Ombre commune au pied des survivants. */
const SURV_SHADOW = `<ellipse cx="12" cy="20" rx="6" ry="2" fill="#000000" opacity="0.35"/>`;

/** Détails communs du visage (peau halée vue dessus). */
const SURV_FACE = `<rect x="9" y="10" width="6" height="2" fill="#d4a574"/>`;

const SURVIVORS: Record<string, () => string> = {
  // === WARRIOR — bandana rouge, tunique sombre, sabre ===
  warrior_idle: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <!-- Tunique en cuir sombre -->
    <rect x="6" y="11" width="12" height="8" fill="#3d2820"/>
    <rect x="5" y="13" width="14" height="5" fill="#3d2820"/>
    <rect x="6" y="11" width="1" height="8" fill="#241410"/>
    <rect x="17" y="11" width="1" height="8" fill="#241410"/>
    <!-- Ceinture cuir clair -->
    <rect x="6" y="16" width="12" height="1" fill="#6b4a2b"/>
    <!-- Bandana rouge (vue dessus, noeud arrière) -->
    <rect x="7" y="6" width="10" height="4" fill="#c0392b"/>
    <rect x="7" y="6" width="10" height="1" fill="#8b1f17"/>
    <rect x="6" y="7" width="1" height="2" fill="#c0392b"/>
    <rect x="17" y="7" width="1" height="2" fill="#c0392b"/>
    <rect x="11" y="5" width="2" height="2" fill="#8b1f17"/>
    ${SURV_FACE}
    <!-- Sabre côté droit (lame argent + garde) -->
    <rect x="18" y="11" width="1" height="6" fill="#bdc3c7"/>
    <rect x="17" y="10" width="3" height="1" fill="#8a6438"/>
    <!-- Bottes -->
    <rect x="8" y="19" width="3" height="2" fill="#1a1208"/>
    <rect x="13" y="19" width="3" height="2" fill="#1a1208"/>
  `),
  warrior_walk: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <rect x="6" y="11" width="12" height="8" fill="#3d2820"/>
    <rect x="5" y="13" width="14" height="5" fill="#3d2820"/>
    <rect x="6" y="11" width="1" height="8" fill="#241410"/>
    <rect x="17" y="11" width="1" height="8" fill="#241410"/>
    <rect x="6" y="16" width="12" height="1" fill="#6b4a2b"/>
    <rect x="7" y="6" width="10" height="4" fill="#c0392b"/>
    <rect x="7" y="6" width="10" height="1" fill="#8b1f17"/>
    <rect x="6" y="7" width="1" height="2" fill="#c0392b"/>
    <rect x="17" y="7" width="1" height="2" fill="#c0392b"/>
    <rect x="11" y="5" width="2" height="2" fill="#8b1f17"/>
    ${SURV_FACE}
    <rect x="18" y="12" width="1" height="6" fill="#bdc3c7"/>
    <rect x="17" y="11" width="3" height="1" fill="#8a6438"/>
    <!-- Bottes décalées -->
    <rect x="7" y="19" width="3" height="2" fill="#1a1208"/>
    <rect x="14" y="20" width="3" height="2" fill="#1a1208"/>
  `),

  // === RANGER — capuche verte, tunique marron, arc dans le dos ===
  ranger_idle: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <!-- Tunique marron clair -->
    <rect x="6" y="11" width="12" height="8" fill="#7a5a38"/>
    <rect x="5" y="13" width="14" height="5" fill="#7a5a38"/>
    <rect x="6" y="11" width="1" height="8" fill="#5a3f25"/>
    <rect x="17" y="11" width="1" height="8" fill="#5a3f25"/>
    <!-- Ceinture verte (carquois) -->
    <rect x="6" y="16" width="12" height="1" fill="#3d5a35"/>
    <!-- Capuche verte (forme arrondie vue dessus) -->
    <rect x="7" y="6" width="10" height="4" fill="#3d5a35"/>
    <rect x="6" y="7" width="12" height="3" fill="#3d5a35"/>
    <rect x="7" y="5" width="10" height="1" fill="#2a3f25"/>
    <rect x="8" y="9" width="8" height="1" fill="#2a3f25"/>
    ${SURV_FACE}
    <!-- Arc dans le dos (visible derrière l'épaule) -->
    <rect x="4" y="9" width="1" height="9" fill="#6b4a2b"/>
    <rect x="3" y="10" width="1" height="7" fill="#8a6438"/>
    <!-- Corde de l'arc -->
    <rect x="5" y="10" width="1" height="7" fill="#ecdcb5" opacity="0.6"/>
    <!-- Plumes du carquois (droit) -->
    <rect x="19" y="11" width="1" height="3" fill="#ecdcb5"/>
    <rect x="20" y="12" width="1" height="2" fill="#ecdcb5"/>
    <!-- Bottes -->
    <rect x="8" y="19" width="3" height="2" fill="#3d2820"/>
    <rect x="13" y="19" width="3" height="2" fill="#3d2820"/>
  `),
  ranger_walk: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <rect x="6" y="11" width="12" height="8" fill="#7a5a38"/>
    <rect x="5" y="13" width="14" height="5" fill="#7a5a38"/>
    <rect x="6" y="11" width="1" height="8" fill="#5a3f25"/>
    <rect x="17" y="11" width="1" height="8" fill="#5a3f25"/>
    <rect x="6" y="16" width="12" height="1" fill="#3d5a35"/>
    <rect x="7" y="6" width="10" height="4" fill="#3d5a35"/>
    <rect x="6" y="7" width="12" height="3" fill="#3d5a35"/>
    <rect x="7" y="5" width="10" height="1" fill="#2a3f25"/>
    <rect x="8" y="9" width="8" height="1" fill="#2a3f25"/>
    ${SURV_FACE}
    <rect x="4" y="9" width="1" height="9" fill="#6b4a2b"/>
    <rect x="3" y="10" width="1" height="7" fill="#8a6438"/>
    <rect x="5" y="10" width="1" height="7" fill="#ecdcb5" opacity="0.6"/>
    <rect x="19" y="11" width="1" height="3" fill="#ecdcb5"/>
    <rect x="20" y="12" width="1" height="2" fill="#ecdcb5"/>
    <!-- Bottes décalées -->
    <rect x="7" y="19" width="3" height="2" fill="#3d2820"/>
    <rect x="14" y="20" width="3" height="2" fill="#3d2820"/>
  `),

  // === MEDIC — bonnet blanc + croix rouge, tablier blanc, sacoche ===
  medic_idle: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <!-- Tablier blanc (sur tunique gris-bleu) -->
    <rect x="6" y="11" width="12" height="8" fill="#5a6a7a"/>
    <rect x="5" y="13" width="14" height="5" fill="#5a6a7a"/>
    <rect x="6" y="11" width="1" height="8" fill="#3a4a5a"/>
    <rect x="17" y="11" width="1" height="8" fill="#3a4a5a"/>
    <!-- Tablier blanc central -->
    <rect x="9" y="11" width="6" height="8" fill="#ecdcb5"/>
    <rect x="9" y="11" width="6" height="1" fill="#ffffff"/>
    <!-- Petite croix rouge sur le tablier -->
    <rect x="11" y="14" width="2" height="1" fill="#c0392b"/>
    <rect x="11" y="13" width="2" height="3" fill="#c0392b" opacity="0.85"/>
    <rect x="10" y="14" width="4" height="1" fill="#c0392b"/>
    <!-- Bonnet blanc avec croix rouge -->
    <rect x="7" y="6" width="10" height="4" fill="#ffffff"/>
    <rect x="6" y="7" width="12" height="3" fill="#ffffff"/>
    <rect x="7" y="5" width="10" height="1" fill="#d8d8d8"/>
    <!-- Croix rouge sur le bonnet -->
    <rect x="11" y="7" width="2" height="2" fill="#c0392b"/>
    <rect x="10" y="7" width="4" height="1" fill="#c0392b"/>
    ${SURV_FACE}
    <!-- Sacoche médicale côté droit -->
    <rect x="18" y="13" width="2" height="4" fill="#8a6438"/>
    <rect x="18" y="13" width="2" height="1" fill="#a98a4c"/>
    <rect x="18" y="14" width="2" height="1" fill="#c0392b"/>
    <!-- Bottes -->
    <rect x="8" y="19" width="3" height="2" fill="#1a1208"/>
    <rect x="13" y="19" width="3" height="2" fill="#1a1208"/>
  `),
  medic_walk: () => svgN(SURVIVOR_SIZE, `
    ${SURV_SHADOW}
    <rect x="6" y="11" width="12" height="8" fill="#5a6a7a"/>
    <rect x="5" y="13" width="14" height="5" fill="#5a6a7a"/>
    <rect x="6" y="11" width="1" height="8" fill="#3a4a5a"/>
    <rect x="17" y="11" width="1" height="8" fill="#3a4a5a"/>
    <rect x="9" y="11" width="6" height="8" fill="#ecdcb5"/>
    <rect x="9" y="11" width="6" height="1" fill="#ffffff"/>
    <rect x="11" y="14" width="2" height="1" fill="#c0392b"/>
    <rect x="11" y="13" width="2" height="3" fill="#c0392b" opacity="0.85"/>
    <rect x="10" y="14" width="4" height="1" fill="#c0392b"/>
    <rect x="7" y="6" width="10" height="4" fill="#ffffff"/>
    <rect x="6" y="7" width="12" height="3" fill="#ffffff"/>
    <rect x="7" y="5" width="10" height="1" fill="#d8d8d8"/>
    <rect x="11" y="7" width="2" height="2" fill="#c0392b"/>
    <rect x="10" y="7" width="4" height="1" fill="#c0392b"/>
    ${SURV_FACE}
    <rect x="18" y="14" width="2" height="4" fill="#8a6438"/>
    <rect x="18" y="14" width="2" height="1" fill="#a98a4c"/>
    <rect x="18" y="15" width="2" height="1" fill="#c0392b"/>
    <!-- Bottes décalées -->
    <rect x="7" y="19" width="3" height="2" fill="#1a1208"/>
    <rect x="14" y="20" width="3" height="2" fill="#1a1208"/>
  `),
};

// ============================================================
// BÂTIMENTS — sprites pixel art 48×48, deux états (built + foundation)
// ============================================================
// Vue top-down 3/4 (légère perspective). Chaque bâtiment a :
//   - une variante "built"      : bâtiment terminé, palette pleine
//   - une variante "foundation" : fondations grisées (pierres au sol)
// Ombre incluse au pied du sprite pour un effet 2.5D léger.

const BUILDING_SIZE = 48;

/** Ombre commune au pied des bâtiments (ellipse semi-transparente) */
const SHADOW = `<ellipse cx="24" cy="44" rx="18" ry="3" fill="#000000" opacity="0.35"/>`;

/** Fondations grisées : pierres rectangulaires au sol, sans toit ni détails */
function foundationSvg(): string {
  return svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Plateforme de pierres -->
    <rect x="6" y="30" width="36" height="12" fill="${PALETTE.stoneDark}"/>
    <rect x="7" y="29" width="34" height="2" fill="${PALETTE.stoneLight}" opacity="0.5"/>
    <!-- Pierres individuelles (effet maçonnerie) -->
    <rect x="7" y="31" width="6" height="4" fill="${PALETTE.stoneBase}"/>
    <rect x="14" y="31" width="7" height="4" fill="${PALETTE.stoneLight}" opacity="0.7"/>
    <rect x="22" y="31" width="6" height="4" fill="${PALETTE.stoneBase}"/>
    <rect x="29" y="31" width="7" height="4" fill="${PALETTE.stoneLight}" opacity="0.7"/>
    <rect x="37" y="31" width="4" height="4" fill="${PALETTE.stoneBase}"/>
    <rect x="7" y="36" width="8" height="4" fill="${PALETTE.stoneLight}" opacity="0.6"/>
    <rect x="16" y="36" width="6" height="4" fill="${PALETTE.stoneBase}"/>
    <rect x="23" y="36" width="8" height="4" fill="${PALETTE.stoneLight}" opacity="0.6"/>
    <rect x="32" y="36" width="9" height="4" fill="${PALETTE.stoneBase}"/>
    <!-- Pancarte "fondations" plantée -->
    <rect x="22" y="14" width="2" height="18" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="10" width="18" height="8" fill="${PALETTE.woodBase}"/>
    <rect x="14" y="10" width="18" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="16" y="13" width="14" height="2" fill="${PALETTE.woodDark}"/>
  `);
}

const BUILDINGS: Record<string, () => string> = {
  // Atelier — forge avec enclume + cheminée fumante
  workshop_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Mur arrière (planches verticales) -->
    <rect x="6" y="18" width="36" height="22" fill="${PALETTE.woodBase}"/>
    <rect x="8" y="18" width="2" height="22" fill="${PALETTE.woodLight}"/>
    <rect x="14" y="18" width="2" height="22" fill="${PALETTE.woodDark}"/>
    <rect x="20" y="18" width="2" height="22" fill="${PALETTE.woodLight}"/>
    <rect x="26" y="18" width="2" height="22" fill="${PALETTE.woodDark}"/>
    <rect x="32" y="18" width="2" height="22" fill="${PALETTE.woodLight}"/>
    <rect x="38" y="18" width="2" height="22" fill="${PALETTE.woodDark}"/>
    <!-- Toit en pente (chaume) -->
    <rect x="4" y="12" width="40" height="8" fill="${PALETTE.thatchBase}"/>
    <rect x="4" y="12" width="40" height="2" fill="${PALETTE.thatchLight}"/>
    <rect x="4" y="18" width="40" height="2" fill="${PALETTE.thatchDark}"/>
    <!-- Cheminée + fumée -->
    <rect x="32" y="6" width="6" height="8" fill="${PALETTE.stoneBase}"/>
    <rect x="32" y="6" width="6" height="1" fill="${PALETTE.stoneLight}"/>
    <rect x="33" y="3" width="2" height="2" fill="${PALETTE.metalLight}" opacity="0.55"/>
    <rect x="34" y="0" width="3" height="2" fill="${PALETTE.metalLight}" opacity="0.35"/>
    <!-- Enclume devant -->
    <rect x="18" y="32" width="12" height="4" fill="${PALETTE.metalDark}"/>
    <rect x="20" y="36" width="8" height="3" fill="${PALETTE.metalBase}"/>
    <rect x="22" y="39" width="4" height="2" fill="${PALETTE.metalDark}"/>
    <rect x="18" y="31" width="6" height="2" fill="${PALETTE.metalBase}"/>
    <!-- Lueur de forge (fenêtre) -->
    <rect x="10" y="24" width="6" height="6" fill="${PALETTE.forgeGlow}"/>
    <rect x="11" y="25" width="4" height="4" fill="${PALETTE.forgeBright}"/>
    <rect x="12" y="26" width="2" height="2" fill="#ffffff" opacity="0.7"/>
  `),
  workshop_foundation: () => foundationSvg(),

  // Coffre — cabane de stockage avec gros coffre devant
  storage_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Cabane en bois -->
    <rect x="8" y="20" width="32" height="20" fill="${PALETTE.woodBase}"/>
    <rect x="8" y="20" width="32" height="2" fill="${PALETTE.woodLight}"/>
    <rect x="8" y="38" width="32" height="2" fill="${PALETTE.woodDark}"/>
    <!-- Planches horizontales -->
    <rect x="8" y="26" width="32" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="8" y="32" width="32" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Toit plat avec relief -->
    <rect x="6" y="16" width="36" height="6" fill="${PALETTE.thatchDark}"/>
    <rect x="6" y="16" width="36" height="2" fill="${PALETTE.thatchBase}"/>
    <!-- Porte fermée -->
    <rect x="22" y="28" width="8" height="12" fill="${PALETTE.woodDark}"/>
    <rect x="28" y="33" width="1" height="2" fill="${PALETTE.forgeBright}"/>
    <!-- Gros coffre devant -->
    <rect x="14" y="36" width="12" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="34" width="12" height="3" fill="${PALETTE.woodLight}"/>
    <rect x="14" y="34" width="12" height="1" fill="${PALETTE.forgeBright}" opacity="0.7"/>
    <rect x="19" y="36" width="2" height="2" fill="${PALETTE.forgeBright}"/>
    <!-- Cerclages métalliques -->
    <rect x="16" y="37" width="1" height="5" fill="${PALETTE.metalDark}"/>
    <rect x="23" y="37" width="1" height="5" fill="${PALETTE.metalDark}"/>
  `),
  storage_foundation: () => foundationSvg(),

  // Dortoir — tente conique avec hamacs derrière
  dormitory_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Tente arrière (canvas rouge) -->
    <rect x="10" y="22" width="28" height="18" fill="${PALETTE.canvasRed}"/>
    <rect x="10" y="22" width="28" height="2" fill="${PALETTE.canvasRedDark}" opacity="0.4"/>
    <rect x="10" y="22" width="2" height="18" fill="${PALETTE.canvasRedDark}"/>
    <rect x="36" y="22" width="2" height="18" fill="${PALETTE.canvasRedDark}"/>
    <!-- Sommet conique de la tente -->
    <rect x="14" y="14" width="20" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="12" y="16" width="24" height="2" fill="${PALETTE.canvasRed}"/>
    <rect x="11" y="18" width="26" height="2" fill="${PALETTE.canvasRed}"/>
    <rect x="10" y="20" width="28" height="2" fill="${PALETTE.canvasRed}"/>
    <rect x="22" y="10" width="4" height="6" fill="${PALETTE.canvasRedDark}"/>
    <!-- Mât central avec fanion -->
    <rect x="23" y="6" width="2" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="25" y="6" width="4" height="3" fill="${PALETTE.forgeBright}"/>
    <rect x="26" y="9" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <!-- Ouverture / pan rabattu -->
    <rect x="18" y="26" width="12" height="14" fill="${PALETTE.canvasRedDark}"/>
    <rect x="20" y="28" width="8" height="11" fill="#1a1a2a" opacity="0.85"/>
    <!-- Hamacs entrevus (lignes blanches) -->
    <rect x="21" y="31" width="6" height="1" fill="${PALETTE.sandShell}" opacity="0.7"/>
    <rect x="21" y="35" width="6" height="1" fill="${PALETTE.sandShell}" opacity="0.5"/>
    <!-- Cordes/haubans -->
    <rect x="8" y="22" width="2" height="16" fill="${PALETTE.woodDark}" opacity="0.7"/>
    <rect x="38" y="22" width="2" height="16" fill="${PALETTE.woodDark}" opacity="0.7"/>
  `),
  dormitory_foundation: () => foundationSvg(),

  // Vigie — tour de guet en bois (étagée + drapeau)
  watchtower_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Base élargie -->
    <rect x="10" y="34" width="28" height="8" fill="${PALETTE.woodDark}"/>
    <rect x="10" y="34" width="28" height="1" fill="${PALETTE.woodLight}"/>
    <!-- Premier étage -->
    <rect x="14" y="22" width="20" height="12" fill="${PALETTE.woodBase}"/>
    <rect x="14" y="22" width="20" height="2" fill="${PALETTE.woodLight}"/>
    <rect x="14" y="32" width="20" height="2" fill="${PALETTE.woodDark}"/>
    <!-- Diagonales (charpente apparente) -->
    <rect x="16" y="24" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="18" y="26" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="20" y="28" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="30" y="24" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="28" y="26" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="26" y="28" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <!-- Plateforme d'observation (créneaux) -->
    <rect x="12" y="16" width="24" height="6" fill="${PALETTE.woodBase}"/>
    <rect x="12" y="14" width="3" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="17" y="14" width="3" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="22" y="14" width="3" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="27" y="14" width="3" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="32" y="14" width="3" height="3" fill="${PALETTE.woodDark}"/>
    <!-- Lanterne (lueur) -->
    <rect x="22" y="18" width="4" height="3" fill="${PALETTE.forgeBright}"/>
    <rect x="23" y="19" width="2" height="1" fill="#ffffff" opacity="0.8"/>
    <!-- Mât drapeau -->
    <rect x="23" y="4" width="2" height="12" fill="${PALETTE.woodDark}"/>
    <rect x="25" y="4" width="6" height="4" fill="${PALETTE.canvasRed}"/>
    <rect x="25" y="4" width="6" height="1" fill="${PALETTE.canvasRedDark}"/>
    <rect x="27" y="5" width="2" height="2" fill="#000000"/>
  `),
  watchtower_foundation: () => foundationSvg(),

  // Taverne — bâtiment avec enseigne suspendue et tonneau
  tavern_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Mur principal -->
    <rect x="6" y="20" width="36" height="20" fill="${PALETTE.woodBase}"/>
    <rect x="6" y="20" width="36" height="2" fill="${PALETTE.woodLight}"/>
    <rect x="6" y="38" width="36" height="2" fill="${PALETTE.woodDark}"/>
    <!-- Planches verticales -->
    <rect x="12" y="22" width="1" height="18" fill="${PALETTE.woodDark}"/>
    <rect x="18" y="22" width="1" height="18" fill="${PALETTE.woodDark}"/>
    <rect x="30" y="22" width="1" height="18" fill="${PALETTE.woodDark}"/>
    <rect x="36" y="22" width="1" height="18" fill="${PALETTE.woodDark}"/>
    <!-- Toit en pente (chaume) -->
    <rect x="4" y="12" width="40" height="9" fill="${PALETTE.thatchBase}"/>
    <rect x="4" y="12" width="40" height="2" fill="${PALETTE.thatchLight}"/>
    <rect x="4" y="19" width="40" height="2" fill="${PALETTE.thatchDark}"/>
    <rect x="8" y="14" width="2" height="5" fill="${PALETTE.thatchDark}"/>
    <rect x="26" y="14" width="2" height="5" fill="${PALETTE.thatchDark}"/>
    <rect x="38" y="14" width="2" height="5" fill="${PALETTE.thatchDark}"/>
    <!-- Fenêtres éclairées -->
    <rect x="10" y="26" width="6" height="6" fill="${PALETTE.forgeGlow}"/>
    <rect x="11" y="27" width="4" height="4" fill="${PALETTE.forgeBright}"/>
    <rect x="12" y="29" width="2" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="32" y="26" width="6" height="6" fill="${PALETTE.forgeGlow}"/>
    <rect x="33" y="27" width="4" height="4" fill="${PALETTE.forgeBright}"/>
    <rect x="34" y="29" width="2" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Porte centrale -->
    <rect x="21" y="28" width="6" height="12" fill="${PALETTE.woodDark}"/>
    <rect x="25" y="33" width="1" height="2" fill="${PALETTE.forgeBright}"/>
    <!-- Enseigne suspendue (chope) -->
    <rect x="20" y="22" width="2" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="26" y="22" width="2" height="3" fill="${PALETTE.woodDark}"/>
    <rect x="19" y="25" width="10" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="19" y="25" width="10" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="21" y="27" width="6" height="3" fill="${PALETTE.forgeBright}"/>
    <rect x="22" y="28" width="4" height="1" fill="${PALETTE.thatchLight}"/>
    <!-- Tonneau devant -->
    <rect x="36" y="34" width="6" height="6" fill="${PALETTE.woodLight}"/>
    <rect x="36" y="34" width="6" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="36" y="37" width="6" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="36" y="39" width="6" height="1" fill="${PALETTE.metalDark}"/>
  `),
  tavern_foundation: () => foundationSvg(),

  // Chantier Naval — cale sèche TEASER acte 2 (toujours en état "fondations / verrouillé").
  // Coque de bateau en construction sur des traverses en bois, grues rudimentaires,
  // cadenas géant superposé pour signifier "verrouillé".
  shipyard_locked: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Traverses (cales en bois sur lesquelles repose la coque) -->
    <rect x="6" y="38" width="36" height="4" fill="${PALETTE.woodDark}"/>
    <rect x="6" y="38" width="36" height="1" fill="${PALETTE.woodBase}"/>
    <rect x="10" y="34" width="2" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="22" y="34" width="2" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="36" y="34" width="2" height="6" fill="${PALETTE.woodDark}"/>
    <!-- Coque du bateau en construction (forme arrondie) -->
    <rect x="10" y="26" width="28" height="10" fill="${PALETTE.woodBase}"/>
    <rect x="12" y="24" width="24" height="2" fill="${PALETTE.woodLight}"/>
    <rect x="8" y="28" width="2" height="6" fill="${PALETTE.woodBase}"/>
    <rect x="38" y="28" width="2" height="6" fill="${PALETTE.woodBase}"/>
    <!-- Ribs / quille incomplète (côtes apparentes) -->
    <rect x="13" y="26" width="1" height="10" fill="${PALETTE.woodDark}"/>
    <rect x="18" y="26" width="1" height="10" fill="${PALETTE.woodDark}"/>
    <rect x="23" y="26" width="1" height="10" fill="${PALETTE.woodDark}"/>
    <rect x="28" y="26" width="1" height="10" fill="${PALETTE.woodDark}"/>
    <rect x="33" y="26" width="1" height="10" fill="${PALETTE.woodDark}"/>
    <!-- Mât planté brut (sans voile) -->
    <rect x="23" y="10" width="2" height="16" fill="${PALETTE.woodDark}"/>
    <rect x="19" y="14" width="10" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Grue rudimentaire à gauche -->
    <rect x="3" y="14" width="2" height="22" fill="${PALETTE.woodDark}"/>
    <rect x="3" y="14" width="10" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="11" y="16" width="1" height="6" fill="${PALETTE.metalDark}"/>
    <rect x="9" y="22" width="5" height="3" fill="${PALETTE.woodBase}"/>
    <!-- Cadenas géant superposé (signifie "verrouillé / pas encore disponible") -->
    <rect x="20" y="18" width="8" height="6" fill="${PALETTE.metalDark}"/>
    <rect x="20" y="18" width="8" height="1" fill="${PALETTE.metalLight}"/>
    <rect x="22" y="14" width="4" height="4" fill="none" stroke="${PALETTE.metalDark}" stroke-width="2"/>
    <rect x="23" y="20" width="2" height="3" fill="${PALETTE.metalLight}"/>
  `),

  // Boutique / Shop — étal de marchand avec auvent et marchandises
  shop_built: () => svgN(BUILDING_SIZE, `
    ${SHADOW}
    <!-- Poteaux verticaux -->
    <rect x="6" y="16" width="2" height="24" fill="${PALETTE.woodDark}"/>
    <rect x="40" y="16" width="2" height="24" fill="${PALETTE.woodDark}"/>
    <!-- Auvent rayé (canvas) -->
    <rect x="4" y="12" width="40" height="6" fill="${PALETTE.canvasRedDark}"/>
    <rect x="4" y="12" width="40" height="1" fill="${PALETTE.canvasRed}" opacity="0.7"/>
    <rect x="4" y="14" width="40" height="2" fill="${PALETTE.sandShell}"/>
    <rect x="4" y="16" width="40" height="2" fill="${PALETTE.canvasRed}"/>
    <!-- Frange (pendouilles bas de l'auvent) -->
    <rect x="6" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="12" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="18" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="24" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="30" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <rect x="36" y="18" width="2" height="2" fill="${PALETTE.canvasRedDark}"/>
    <!-- Comptoir en bois -->
    <rect x="6" y="30" width="36" height="10" fill="${PALETTE.woodBase}"/>
    <rect x="6" y="30" width="36" height="2" fill="${PALETTE.woodLight}"/>
    <rect x="6" y="38" width="36" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="12" y="32" width="1" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="24" y="32" width="1" height="6" fill="${PALETTE.woodDark}"/>
    <rect x="36" y="32" width="1" height="6" fill="${PALETTE.woodDark}"/>
    <!-- Marchandises sur le comptoir -->
    <!-- Sac d'or doré -->
    <rect x="9" y="26" width="6" height="4" fill="${PALETTE.forgeGlow}"/>
    <rect x="9" y="24" width="6" height="2" fill="${PALETTE.forgeBright}"/>
    <rect x="11" y="22" width="2" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="11" y="27" width="2" height="1" fill="${PALETTE.forgeBright}"/>
    <!-- Potion rouge -->
    <rect x="18" y="24" width="4" height="6" fill="${PALETTE.canvasRed}"/>
    <rect x="18" y="24" width="4" height="1" fill="${PALETTE.canvasRedDark}"/>
    <rect x="19" y="22" width="2" height="2" fill="${PALETTE.metalDark}"/>
    <rect x="19" y="26" width="1" height="1" fill="#ffffff" opacity="0.7"/>
    <!-- Épée -->
    <rect x="27" y="20" width="1" height="10" fill="${PALETTE.metalLight}"/>
    <rect x="26" y="20" width="3" height="1" fill="${PALETTE.metalLight}"/>
    <rect x="25" y="29" width="5" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="26" y="30" width="3" height="2" fill="${PALETTE.forgeBright}"/>
    <!-- Tonnelet -->
    <rect x="33" y="25" width="6" height="5" fill="${PALETTE.woodLight}"/>
    <rect x="33" y="25" width="6" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="33" y="28" width="6" height="1" fill="${PALETTE.metalDark}"/>
  `),
  shop_foundation: () => foundationSvg(),
};

// ============================================================
// OBJETS INTERACTIFS & DÉCORATIONS (Sprint B1)
// ============================================================
// Sprites pixel art pour objets diégétiques (menus, départ raid) et déco.
// Tailles variables (24 ou 32 px) selon l'objet — moins encombrants que
// les bâtiments. Convention origine = (0.5, 1) (centré horizontal, ancré bas).

const OBJECT_SIZE = 32;

const INTERACTABLES: Record<string, () => string> = {
  // Portail / arche d'entrée vers la jungle (zone départ raid).
  // Deux poteaux en bois, linteau, lianes pendantes, ouverture sombre derrière.
  raid_portal: () => svgN(OBJECT_SIZE, `
    <!-- Ombre au sol -->
    <ellipse cx="16" cy="30" rx="12" ry="2" fill="#000000" opacity="0.4"/>
    <!-- Poteaux verticaux -->
    <rect x="4" y="6" width="4" height="22" fill="${PALETTE.woodDark}"/>
    <rect x="4" y="6" width="1" height="22" fill="${PALETTE.woodBase}"/>
    <rect x="24" y="6" width="4" height="22" fill="${PALETTE.woodDark}"/>
    <rect x="24" y="6" width="1" height="22" fill="${PALETTE.woodBase}"/>
    <!-- Linteau horizontal -->
    <rect x="2" y="4" width="28" height="4" fill="${PALETTE.woodDark}"/>
    <rect x="2" y="4" width="28" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="2" y="7" width="28" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Renforts en croix -->
    <rect x="8" y="10" width="2" height="2" fill="${PALETTE.metalDark}"/>
    <rect x="22" y="10" width="2" height="2" fill="${PALETTE.metalDark}"/>
    <!-- Ouverture sombre (chemin qui s'enfonce) -->
    <rect x="8" y="8" width="16" height="20" fill="${PALETTE.grassDetail}"/>
    <rect x="9" y="10" width="14" height="18" fill="#0a1808" opacity="0.85"/>
    <!-- Lianes vertes pendantes -->
    <rect x="6" y="4" width="2" height="6" fill="${PALETTE.grassLight}"/>
    <rect x="7" y="9" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <rect x="10" y="4" width="2" height="4" fill="${PALETTE.grassLight}"/>
    <rect x="15" y="4" width="2" height="5" fill="${PALETTE.grassDark}"/>
    <rect x="20" y="4" width="2" height="4" fill="${PALETTE.grassLight}"/>
    <rect x="24" y="4" width="2" height="7" fill="${PALETTE.grassLight}"/>
    <rect x="25" y="10" width="2" height="3" fill="${PALETTE.grassLight}"/>
    <!-- Touffes de feuilles plus claires -->
    <rect x="6" y="3" width="3" height="2" fill="${PALETTE.grassLight}"/>
    <rect x="14" y="3" width="3" height="2" fill="${PALETTE.grassLight}"/>
    <rect x="23" y="3" width="3" height="2" fill="${PALETTE.grassLight}"/>
    <!-- Faible lueur d'entrée (lanterne au sol) -->
    <rect x="14" y="22" width="4" height="3" fill="${PALETTE.forgeGlow}" opacity="0.7"/>
    <rect x="15" y="23" width="2" height="1" fill="${PALETTE.forgeBright}"/>
  `),

  // Panneau d'affichage — poteau en bois avec planche cloutée + papiers.
  signboard: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="30" rx="6" ry="1.5" fill="#000000" opacity="0.35"/>
    <!-- Poteau central -->
    <rect x="14" y="18" width="4" height="12" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="18" width="1" height="12" fill="${PALETTE.woodBase}"/>
    <!-- Panneau (planches horizontales) -->
    <rect x="4" y="6" width="24" height="14" fill="${PALETTE.woodBase}"/>
    <rect x="4" y="6" width="24" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="4" y="12" width="24" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="4" y="19" width="24" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Bordures -->
    <rect x="4" y="6" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <rect x="27" y="6" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <!-- Clous métalliques aux coins -->
    <rect x="5" y="7" width="1" height="1" fill="${PALETTE.metalLight}"/>
    <rect x="26" y="7" width="1" height="1" fill="${PALETTE.metalLight}"/>
    <rect x="5" y="18" width="1" height="1" fill="${PALETTE.metalLight}"/>
    <rect x="26" y="18" width="1" height="1" fill="${PALETTE.metalLight}"/>
    <!-- Papiers / avis épinglés -->
    <rect x="7" y="8" width="8" height="4" fill="${PALETTE.sandShell}"/>
    <rect x="7" y="8" width="8" height="1" fill="#ffffff" opacity="0.7"/>
    <rect x="8" y="10" width="6" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="17" y="8" width="8" height="4" fill="${PALETTE.sandShell}" opacity="0.85"/>
    <rect x="18" y="10" width="6" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="9" y="13" width="6" height="5" fill="${PALETTE.canvasRed}" opacity="0.7"/>
    <rect x="10" y="14" width="4" height="1" fill="${PALETTE.sandShell}"/>
    <rect x="17" y="14" width="8" height="4" fill="${PALETTE.sandShell}"/>
    <rect x="18" y="15" width="6" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="18" y="17" width="4" height="1" fill="${PALETTE.woodDark}"/>
  `),

  // Table de navigation — table en bois avec carte dépliée + boussole.
  nav_table: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="30" rx="10" ry="2" fill="#000000" opacity="0.4"/>
    <!-- Pieds de table -->
    <rect x="6" y="22" width="2" height="8" fill="${PALETTE.woodDark}"/>
    <rect x="24" y="22" width="2" height="8" fill="${PALETTE.woodDark}"/>
    <!-- Plateau de table -->
    <rect x="3" y="18" width="26" height="6" fill="${PALETTE.woodBase}"/>
    <rect x="3" y="18" width="26" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="3" y="23" width="26" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Bord avant -->
    <rect x="3" y="24" width="26" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Carte dépliée sur la table -->
    <rect x="5" y="12" width="22" height="8" fill="${PALETTE.sandShell}"/>
    <rect x="5" y="12" width="22" height="1" fill="#ffffff" opacity="0.6"/>
    <rect x="5" y="19" width="22" height="1" fill="${PALETTE.pathDark}"/>
    <!-- Côtes / contours dessinés (lignes brunes) -->
    <rect x="7" y="14" width="6" height="1" fill="${PALETTE.pathDark}"/>
    <rect x="8" y="15" width="4" height="1" fill="${PALETTE.pathDark}"/>
    <rect x="17" y="15" width="8" height="1" fill="${PALETTE.pathDark}"/>
    <rect x="19" y="16" width="4" height="1" fill="${PALETTE.pathDark}"/>
    <!-- Route en pointillés -->
    <rect x="10" y="17" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="13" y="17" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="16" y="17" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="19" y="17" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <!-- X au trésor -->
    <rect x="22" y="14" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="23" y="15" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="22" y="16" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="24" y="14" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="24" y="16" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <!-- Boussole posée à gauche -->
    <rect x="4" y="10" width="6" height="6" fill="${PALETTE.metalBase}"/>
    <rect x="5" y="11" width="4" height="4" fill="${PALETTE.metalLight}"/>
    <rect x="6" y="12" width="2" height="2" fill="${PALETTE.canvasRed}"/>
    <rect x="6" y="13" width="2" height="1" fill="${PALETTE.metalDark}"/>
    <!-- Plume / lanterne droite -->
    <rect x="24" y="9" width="2" height="6" fill="${PALETTE.woodLight}"/>
    <rect x="23" y="8" width="4" height="2" fill="${PALETTE.sandShell}"/>
  `),

  // Feu de camp — pierres en cercle, bûches + flammes vives.
  campfire: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="30" rx="10" ry="2" fill="#000000" opacity="0.45"/>
    <!-- Cercle de pierres -->
    <rect x="5" y="22" width="4" height="3" fill="${PALETTE.rockBase}"/>
    <rect x="5" y="22" width="4" height="1" fill="${PALETTE.rockLight}"/>
    <rect x="10" y="24" width="4" height="3" fill="${PALETTE.rockBase}"/>
    <rect x="10" y="24" width="4" height="1" fill="${PALETTE.rockLight}"/>
    <rect x="14" y="25" width="5" height="3" fill="${PALETTE.rockBase}"/>
    <rect x="14" y="25" width="5" height="1" fill="${PALETTE.rockLight}"/>
    <rect x="19" y="24" width="4" height="3" fill="${PALETTE.rockBase}"/>
    <rect x="19" y="24" width="4" height="1" fill="${PALETTE.rockLight}"/>
    <rect x="23" y="22" width="4" height="3" fill="${PALETTE.rockBase}"/>
    <rect x="23" y="22" width="4" height="1" fill="${PALETTE.rockLight}"/>
    <!-- Bûches en X au sol -->
    <rect x="8" y="20" width="16" height="2" fill="${PALETTE.woodDark}"/>
    <rect x="8" y="20" width="16" height="1" fill="${PALETTE.woodBase}"/>
    <rect x="10" y="22" width="12" height="2" fill="${PALETTE.woodDark}"/>
    <!-- Bûche transversale -->
    <rect x="14" y="18" width="2" height="6" fill="${PALETTE.woodLight}"/>
    <rect x="16" y="19" width="2" height="5" fill="${PALETTE.woodBase}"/>
    <!-- Flammes vives (orangé → jaune → blanc) -->
    <rect x="13" y="14" width="6" height="6" fill="${PALETTE.forgeGlow}"/>
    <rect x="14" y="11" width="4" height="3" fill="${PALETTE.forgeGlow}"/>
    <rect x="14" y="13" width="4" height="5" fill="${PALETTE.forgeBright}"/>
    <rect x="15" y="8" width="2" height="3" fill="${PALETTE.forgeBright}"/>
    <rect x="15" y="14" width="2" height="4" fill="#ffffff" opacity="0.85"/>
    <!-- Étincelles -->
    <rect x="11" y="13" width="1" height="1" fill="${PALETTE.forgeGlow}"/>
    <rect x="20" y="15" width="1" height="1" fill="${PALETTE.forgeGlow}"/>
    <rect x="12" y="10" width="1" height="1" fill="${PALETTE.forgeBright}"/>
    <rect x="20" y="11" width="1" height="1" fill="${PALETTE.forgeBright}"/>
    <rect x="15" y="5" width="1" height="1" fill="${PALETTE.forgeBright}" opacity="0.7"/>
  `),
};

const DECORATIONS: Record<string, () => string> = {
  // Palmier — tronc en damier + couronne de palmes en éventail.
  palm_tree: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="30" rx="8" ry="2" fill="#000000" opacity="0.35"/>
    <!-- Tronc texturé -->
    <rect x="14" y="14" width="4" height="16" fill="${PALETTE.woodBase}"/>
    <rect x="14" y="14" width="1" height="16" fill="${PALETTE.woodLight}"/>
    <rect x="17" y="14" width="1" height="16" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="17" width="4" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="21" width="4" height="1" fill="${PALETTE.woodDark}"/>
    <rect x="14" y="25" width="4" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Noix de coco -->
    <rect x="13" y="13" width="2" height="2" fill="#5a3a1f"/>
    <rect x="17" y="13" width="2" height="2" fill="#5a3a1f"/>
    <!-- Couronne de palmes (5 directions) -->
    <!-- Palme haut -->
    <rect x="15" y="2" width="2" height="6" fill="${PALETTE.grassBase}"/>
    <rect x="14" y="4" width="4" height="2" fill="${PALETTE.grassBase}"/>
    <rect x="13" y="6" width="6" height="2" fill="${PALETTE.grassLight}"/>
    <!-- Palme gauche -->
    <rect x="2" y="9" width="12" height="2" fill="${PALETTE.grassBase}"/>
    <rect x="2" y="11" width="10" height="2" fill="${PALETTE.grassLight}"/>
    <rect x="4" y="13" width="8" height="1" fill="${PALETTE.grassDetail}"/>
    <!-- Palme droite -->
    <rect x="18" y="9" width="12" height="2" fill="${PALETTE.grassBase}"/>
    <rect x="20" y="11" width="10" height="2" fill="${PALETTE.grassLight}"/>
    <rect x="20" y="13" width="8" height="1" fill="${PALETTE.grassDetail}"/>
    <!-- Palme bas-gauche -->
    <rect x="4" y="14" width="10" height="2" fill="${PALETTE.grassBase}"/>
    <rect x="6" y="16" width="8" height="1" fill="${PALETTE.grassDetail}"/>
    <!-- Palme bas-droite -->
    <rect x="18" y="14" width="10" height="2" fill="${PALETTE.grassBase}"/>
    <rect x="18" y="16" width="8" height="1" fill="${PALETTE.grassDetail}"/>
  `),

  // Tonneau pirate — bois clair avec cerclages métalliques.
  barrel: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="28" rx="6" ry="1.5" fill="#000000" opacity="0.4"/>
    <rect x="10" y="14" width="12" height="14" fill="${PALETTE.woodLight}"/>
    <rect x="10" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <rect x="21" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <!-- Planches verticales -->
    <rect x="13" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <rect x="16" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <rect x="19" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <!-- Cerclages métal -->
    <rect x="10" y="15" width="12" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="10" y="20" width="12" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="10" y="25" width="12" height="1" fill="${PALETTE.metalDark}"/>
    <!-- Highlight haut -->
    <rect x="11" y="14" width="10" height="1" fill="${PALETTE.sandShell}" opacity="0.6"/>
  `),

  // Caisse en bois — cubique avec cerclages diagonaux.
  crate: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="28" rx="6" ry="1.5" fill="#000000" opacity="0.4"/>
    <rect x="9" y="14" width="14" height="14" fill="${PALETTE.woodBase}"/>
    <rect x="9" y="14" width="14" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="9" y="27" width="14" height="1" fill="${PALETTE.woodDark}"/>
    <!-- Bordures -->
    <rect x="9" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <rect x="22" y="14" width="1" height="14" fill="${PALETTE.woodDark}"/>
    <!-- Renforts diagonaux -->
    <rect x="10" y="15" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="11" y="16" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="12" y="17" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="13" y="18" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="14" y="19" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="15" y="20" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="16" y="21" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="17" y="22" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="18" y="23" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="19" y="24" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="20" y="25" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="21" y="26" width="1" height="1" fill="${PALETTE.woodLight}"/>
    <!-- Clous aux coins -->
    <rect x="10" y="15" width="1" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="21" y="15" width="1" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="10" y="26" width="1" height="1" fill="${PALETTE.metalDark}"/>
    <rect x="21" y="26" width="1" height="1" fill="${PALETTE.metalDark}"/>
  `),

  // Filet de pêche — maillage de cordes brunes au sol avec quelques flotteurs.
  fishing_net: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="26" rx="12" ry="3" fill="#000000" opacity="0.3"/>
    <!-- Maillage horizontal/vertical -->
    <rect x="4" y="20" width="24" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="4" y="23" width="24" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="4" y="26" width="24" height="1" fill="${PALETTE.woodLight}"/>
    <rect x="6" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <rect x="10" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <rect x="14" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <rect x="18" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <rect x="22" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <rect x="26" y="18" width="1" height="10" fill="${PALETTE.woodLight}"/>
    <!-- Flotteurs (boules de liège) -->
    <rect x="6" y="17" width="2" height="2" fill="${PALETTE.sandShell}"/>
    <rect x="14" y="17" width="2" height="2" fill="${PALETTE.sandShell}"/>
    <rect x="22" y="17" width="2" height="2" fill="${PALETTE.sandShell}"/>
    <!-- Une étoile de mer rouge prise dans le filet -->
    <rect x="17" y="22" width="2" height="2" fill="${PALETTE.canvasRed}"/>
    <rect x="18" y="21" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="16" y="23" width="1" height="1" fill="${PALETTE.canvasRed}"/>
    <rect x="19" y="23" width="1" height="1" fill="${PALETTE.canvasRed}"/>
  `),

  // Petit rocher décoratif — plus petit qu'une tuile de rocher pleine.
  small_rock: () => svgN(OBJECT_SIZE, `
    <ellipse cx="16" cy="28" rx="6" ry="1.5" fill="#000000" opacity="0.4"/>
    <rect x="11" y="18" width="10" height="10" fill="${PALETTE.rockBase}"/>
    <rect x="13" y="16" width="6" height="2" fill="${PALETTE.rockBase}"/>
    <rect x="9" y="20" width="2" height="6" fill="${PALETTE.rockBase}"/>
    <rect x="21" y="20" width="2" height="6" fill="${PALETTE.rockBase}"/>
    <rect x="13" y="18" width="6" height="1" fill="${PALETTE.rockLight}"/>
    <rect x="11" y="19" width="2" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="14" y="22" width="2" height="2" fill="${PALETTE.rockLight}"/>
    <rect x="11" y="26" width="10" height="2" fill="${PALETTE.rockDark}"/>
    <rect x="18" y="22" width="2" height="2" fill="${PALETTE.rockDark}"/>
  `),
};

// ============================================================
// HELPERS
// ============================================================

function svg(inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${TILE_SIZE}" height="${TILE_SIZE}" viewBox="0 0 ${TILE_SIZE} ${TILE_SIZE}" shape-rendering="crispEdges">${inner}</svg>`;
}

function svgN(size: number, inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">${inner}</svg>`;
}

// ============================================================
// PUBLIC API
// ============================================================

export type IslandTileType = 'sand' | 'grass' | 'path' | 'water' | 'rock';

/** Identifiants des bâtiments rendus sur l'île (Sprint A2 + B1 teaser chantier naval). */
export type IslandBuildingId = 'workshop' | 'storage' | 'dormitory' | 'watchtower' | 'tavern' | 'shop' | 'shipyard';

/** Identifiants des objets interactifs diégétiques (Sprint B1). */
export type IslandInteractableId = 'raid_portal' | 'signboard' | 'nav_table' | 'campfire';

/** Identifiants des décorations non interactives (Sprint B1). */
export type IslandDecorationId = 'palm_tree' | 'barrel' | 'crate' | 'fishing_net' | 'small_rock';

/** Classes de survivants animés sur l'île (Sprint A3). */
export type IslandSurvivorClass = 'warrior' | 'ranger' | 'medic';

export class IslandTileRenderer {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Charge toutes les tuiles, frames capitaine, bâtiments et objets en parallèle. */
  async generateAllTextures(): Promise<void> {
    const tasks: Promise<void>[] = [];
    for (const id of Object.keys(TILES)) {
      tasks.push(this.svgToTexture(`island-tile-${id}`, TILES[id](), TILE_SIZE, TILE_SIZE));
    }
    for (const frame of Object.keys(CAPTAIN)) {
      tasks.push(this.svgToTexture(`island-captain-${frame}`, CAPTAIN[frame](), CAPTAIN_SIZE, CAPTAIN_SIZE));
    }
    for (const id of Object.keys(BUILDINGS)) {
      tasks.push(this.svgToTexture(`island-bld-${id}`, BUILDINGS[id](), BUILDING_SIZE, BUILDING_SIZE));
    }
    for (const id of Object.keys(INTERACTABLES)) {
      tasks.push(this.svgToTexture(`island-int-${id}`, INTERACTABLES[id](), OBJECT_SIZE, OBJECT_SIZE));
    }
    for (const id of Object.keys(DECORATIONS)) {
      tasks.push(this.svgToTexture(`island-dec-${id}`, DECORATIONS[id](), OBJECT_SIZE, OBJECT_SIZE));
    }
    for (const id of Object.keys(SURVIVORS)) {
      tasks.push(this.svgToTexture(`island-surv-${id}`, SURVIVORS[id](), SURVIVOR_SIZE, SURVIVOR_SIZE));
    }
    // Sprint A4 — mouettes 16×8 (rectangulaire)
    for (const id of Object.keys(SEAGULLS)) {
      tasks.push(this.svgToTexture(`island-seagull-${id}`, SEAGULLS[id](), SEAGULL_W, SEAGULL_H));
    }
    await Promise.all(tasks);
  }

  /** Convertit un SVG en texture Phaser via un canvas intermédiaire (sans lissage). */
  private svgToTexture(key: string, svgStr: string, w: number, h: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scene.textures.exists(key)) { resolve(); return; }
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d')!;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, w, h);
        this.scene.textures.addCanvas(key, c);
        resolve();
      };
      img.onerror = () => reject(new Error(`SVG load failed: "${key}"`));
      img.src = `data:image/svg+xml,${encodeURIComponent(svgStr.trim())}`;
    });
  }

  static tileKey(type: IslandTileType): string { return `island-tile-${type}`; }
  /** Sprint A4 — clé de la 2ᵉ frame d'eau pour animer les vagues. */
  static waterAltKey(): string { return 'island-tile-water_alt'; }
  static captainKey(frame: 'idle' | 'walk'): string { return `island-captain-${frame}`; }
  /** Clé de texture d'un bâtiment selon son état (construit ou fondations grisées).
   *  Cas spécial `shipyard` : un seul état `locked` (teaser acte 2). */
  static buildingKey(id: IslandBuildingId, state: 'built' | 'foundation' | 'locked'): string {
    return `island-bld-${id}_${state}`;
  }
  /** Clé de texture d'un objet interactif (panneau, table, feu de camp, portail). */
  static interactableKey(id: IslandInteractableId): string { return `island-int-${id}`; }
  /** Clé de texture d'une décoration (palmier, tonneau, caisse, filet, rocher déco). */
  static decorationKey(id: IslandDecorationId): string { return `island-dec-${id}`; }
  /** Clé de texture d'un survivant selon classe + frame (Sprint A3). */
  static survivorKey(cls: IslandSurvivorClass, frame: 'idle' | 'walk'): string {
    return `island-surv-${cls}_${frame}`;
  }
  /** Sprint A4 — clé d'une frame de mouette (battement up/down). */
  static seagullKey(frame: 'up' | 'down'): string { return `island-seagull-${frame}`; }
  static get CAPTAIN_SIZE(): number { return CAPTAIN_SIZE; }
  static get BUILDING_SIZE(): number { return BUILDING_SIZE; }
  static get OBJECT_SIZE(): number { return OBJECT_SIZE; }
  static get SURVIVOR_SIZE(): number { return SURVIVOR_SIZE; }
  static get SEAGULL_W(): number { return SEAGULL_W; }
  static get SEAGULL_H(): number { return SEAGULL_H; }
}
